import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, XCircle } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { Brand } from "@/components/common";
import { Button } from "@/components/Button";
import { Field, TextInput } from "@/components/Modal";

function Wrap({ children }) {
  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="w-full max-w-sm iks-fade-up">
        <div className="flex justify-center mb-8"><Brand size={56} /></div>
        {children}
      </div>
    </div>
  );
}

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password: pw });
      toast.success("Password updated. Please log in.");
      nav("/");
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Wrap>
      <h2 className="font-display font-bold text-2xl text-slate-50 mb-6">Choose a new password</h2>
      <form onSubmit={submit}>
        <Field label="New password">
          <TextInput required type="password" minLength={6} value={pw} onChange={(e) => setPw(e.target.value)} placeholder="••••••••" data-testid="reset-password-input" />
        </Field>
        <Button type="submit" full loading={busy} data-testid="reset-submit">Update password</Button>
      </form>
    </Wrap>
  );
}

export function SupportVerifyPage() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [status, setStatus] = useState("verifying");

  useEffect(() => {
    api.post("/support/verify-identity", { token }).then(() => setStatus("ok")).catch(() => setStatus("fail"));
  }, [token]);

  return (
    <Wrap>
      <div className="iks-card p-8 text-center" data-testid="support-verify-page">
        {status === "verifying" && <p className="text-slate-400">Verifying your email…</p>}
        {status === "ok" && (
          <div data-testid="support-verify-success">
            <CheckCircle2 className="mx-auto text-emerald-400 mb-4" size={44} />
            <h2 className="font-display font-bold text-xl text-slate-50">Email verified</h2>
            <p className="text-slate-400 text-sm mt-2">Thank you! InfoKeepSake Admin will send you an email when your account is restored.</p>
            <Link to="/"><Button variant="secondary" className="mt-6" full>Back to login</Button></Link>
          </div>
        )}
        {status === "fail" && (
          <div data-testid="support-verify-fail">
            <XCircle className="mx-auto text-red-400 mb-4" size={44} />
            <h2 className="font-display font-bold text-xl text-slate-50">Link expired</h2>
            <p className="text-slate-400 text-sm mt-2">This verification link is invalid or has expired. Please contact support again from the login screen.</p>
            <Link to="/"><Button variant="secondary" className="mt-6" full>Back to login</Button></Link>
          </div>
        )}
      </div>
    </Wrap>
  );
}

export function VerifyEmailPage() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [status, setStatus] = useState("verifying");

  useEffect(() => {
    (async () => {
      try {
        await api.post("/auth/verify-email", { token });
        setStatus("ok");
      } catch {
        setStatus("fail");
      }
    })();
  }, [token]);

  return (
    <Wrap>
      <div className="iks-card p-8 text-center">
        {status === "verifying" && <p className="text-slate-400">Verifying your email…</p>}
        {status === "ok" && (
          <>
            <CheckCircle2 className="mx-auto text-emerald-400 mb-4" size={44} />
            <h2 className="font-display font-bold text-xl text-slate-50">Email verified</h2>
            <p className="text-slate-400 text-sm mt-2">Your account is all set.</p>
            <Link to="/profile"><Button className="mt-6" full>Go to app</Button></Link>
          </>
        )}
        {status === "fail" && (
          <>
            <XCircle className="mx-auto text-red-400 mb-4" size={44} />
            <h2 className="font-display font-bold text-xl text-slate-50">Link expired</h2>
            <p className="text-slate-400 text-sm mt-2">This verification link is invalid or has expired.</p>
            <Link to="/profile"><Button variant="secondary" className="mt-6" full>Back to app</Button></Link>
          </>
        )}
      </div>
    </Wrap>
  );
}
